import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPen,
  faTrash,
  faPlus,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import useSubjectAPI from "./useSubjectAPI";

const EmailAddressesSection = ({ subjectId, form }) => {
  const isCreate = !subjectId;
  const purple = "#2E2868";
  const pillBg = "#ebeaf3";

  const { loadSubject, saveSubjectDirect } = useSubjectAPI(subjectId);

  const [open, setOpen] = useState(true);

  const [emails, setEmails] = useState([
    { email_address: "", preferred: true, id: null, isEditing: isCreate },
  ]);

  const [originalEmails, setOriginalEmails] = useState({});

  /* LOAD DATA (EDIT MODE ONLY) */
  useEffect(() => {
    if (isCreate) return;

    loadSubject().then((res) => {
      if (!res?.subject_emails?.length) return;

      const loadedEmails = res.subject_emails.map((e) => ({
        ...e,
        isEditing: false,
      }));
      setEmails(loadedEmails);
    });
  }, [isCreate, subjectId]);

  /* =========================
      HANDLERS
     ========================= */

  const updateParentForm = (updatedList) => {
    if (isCreate && form?.setValue) {
      form.setValue({
        key: "subject_emails",
        value: updatedList.map((e) => ({
          email_address: e.email_address,
          preferred: e.preferred,
        })),
      });
    }
  };

  const handleAddField = () => {
    const newList = [
      ...emails,
      { email_address: "", preferred: false, id: null, isEditing: true },
    ];
    setEmails(newList);
  };

  const handleRemoveField = async (index) => {
    const emailToRemove = emails[index];
    if (!window.confirm("Are you sure?")) return;

    const newList = emails.filter((_, i) => i !== index);
    setEmails(newList);

    if (isCreate) {
      updateParentForm(newList);
    }

    if (!isCreate && emailToRemove.id) {
      try {
        await saveSubjectDirect(
          {
            subjects: { id: subjectId },
          },
          {
            input_delete: [{ id: emailToRemove.id, key: "subject_emails" }],
          },
        );
      } catch (err) {
        console.error("Delete failed", err);
      }
    }
  };

  const handleChange = (index, field, value) => {
    const newList = [...emails];
    newList[index][field] = value;
    setEmails(newList);
    updateParentForm(newList);
  };

  const toggleEdit = (index) => {
    const newList = [...emails];

    setOriginalEmails((prev) => ({ ...prev, [index]: { ...newList[index] } }));
    newList[index].isEditing = true;
    setEmails(newList);
  };

  const handleCancel = (index) => {
    const newList = [...emails];
    const original = originalEmails[index];
    if (original) {
      newList[index] = { ...original, isEditing: false };
      setEmails(newList);
    } else if (newList[index].id === null && !isCreate) {
      newList.splice(index, 1);
      setEmails(newList);
    } else {
      newList[index].isEditing = false;
      setEmails(newList);
    }
  };

  const saveSingleEmail = async (index) => {
    if (isCreate) {
      const newList = [...emails];
      newList[index].isEditing = false;
      setEmails(newList);
      return;
    }

    const emailToSave = emails[index];
    const payload = {
      subjects: { id: subjectId },
      subject_emails: [
        {
          id: emailToSave.id,
          email_address: emailToSave.email_address,
          preferred: emailToSave.preferred,
        },
      ],
    };

    const res = await saveSubjectDirect(payload);
    if (res) {
      const newList = [...emails];
      newList[index].isEditing = false;
      setEmails(newList);
    } else {
      alert("Save failed");
      handleCancel(index); // Revert UI on failure
    }
  };

  /* RENDER ROW */
  const renderEmailRow = (email, index) => {
    const isEditing = email.isEditing;

    return (
      <div key={index} className="mb-3">
        <div
          className="d-flex align-items-center gap-2"
          style={{ position: "relative" }}
        >
          <div style={{ flex: 1, position: "relative" }}>
            <input
              type="email"
              placeholder="example@mail.com"
              value={email.email_address}
              disabled={!isEditing}
              onChange={(e) =>
                handleChange(index, "email_address", e.target.value)
              }
              className={`form-control rounded-pill ${!isEditing ? "border-0" : ""}`}
              style={{
                backgroundColor: isEditing ? "#fff" : pillBg,
                border: isEditing ? `2px solid ${purple}` : "0",
                paddingRight: isEditing && !isCreate ? "110px" : "40px",
              }}
            />

            {!isEditing && (
              <FontAwesomeIcon
                icon={faPen}
                onClick={() => toggleEdit(index)}
                style={{
                  position: "absolute",
                  right: "15px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  cursor: "pointer",
                  color: purple,
                  fontSize: "14px",
                }}
              />
            )}

            {isEditing && !isCreate && (
              <div
                style={{
                  position: "absolute",
                  right: "6px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  display: "flex",
                  gap: "4px",
                }}
              >
                <button
                  onClick={() => saveSingleEmail(index)}
                  className="btn btn-sm"
                  style={{
                    background: purple,
                    color: "#fff",
                    borderRadius: "8px",
                    fontSize: "11px",
                    padding: "2px 8px",
                  }}
                >
                  Save
                </button>
                <button
                  onClick={() => handleCancel(index)}
                  className="btn btn-sm btn-light"
                  style={{
                    borderRadius: "8px",
                    fontSize: "11px",
                    padding: "2px 8px",
                    border: "1px solid #ccc",
                  }}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>

          <button
            className="btn btn-link text-danger p-0"
            onClick={() => handleRemoveField(index)}
            style={{ visibility: emails.length > 1 ? "visible" : "hidden" }}
          >
            <FontAwesomeIcon
              icon={faTrash}
              style={{ color: purple, cursor: "pointer", fontSize: "16px" }}
            />
          </button>
        </div>

        <div className="form-check mt-1 ms-2">
          <input
            className="form-check-input"
            type="checkbox"
            checked={email.preferred}
            disabled={!isEditing}
            onChange={(e) => handleChange(index, "preferred", e.target.checked)}
            id={`preferred-${index}`}
          />
          <label
            className="form-check-label small"
            htmlFor={`preferred-${index}`}
          >
            Preferred Email
          </label>
        </div>
      </div>
    );
  };

  return (
    <div className="my-3" style={{ maxWidth: "490px" }}>
      <div className="p-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div
            className="d-flex align-items-center"
            onClick={() => setOpen(!open)}
            style={{ cursor: "pointer" }}
          >
            <span
              style={{ fontSize: "30px", color: "#b2a9ff", marginRight: "6px" }}
            >
              ⋮⋮
            </span>
            <h5 className="fw-bold m-0" style={{ color: purple }}>
              Email Addresses
            </h5>
          </div>
          <div
            style={{
              width: "28px",
              height: "28px",
              borderRadius: "8px",
              backgroundColor: purple,
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              transition: "transform 0.3s",
              transform: open ? "rotate(90deg)" : "rotate(0deg)",
            }}
            onClick={() => setOpen(!open)}
          >
            <i className="bi bi-chevron-right" />
          </div>
        </div>

        {open && (
          <>
            {emails.map((email, index) => renderEmailRow(email, index))}

            <div className="text-center mt-3">
              <button
                onClick={handleAddField}
                className="btn btn-sm"
                style={{
                  border: `1px dashed ${purple}`,
                  color: purple,
                  borderRadius: "20px",
                  padding: "5px 20px",
                  backgroundColor: "transparent",
                }}
              >
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Add Another Email
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EmailAddressesSection;
